/**
 * hrisAccessRules — who may open which HRIS screens, and who may act.
 *
 * Payroll data (rates, net pay, deductions, government numbers) is
 * confidential, so the module is default-DENY: only the accounts listed here
 * can open it, and some accounts are limited to a subset of screens.
 *
 *   · FULL access (every screen): the owner + HR/Finance.
 *   · LIMITED access: a per-user allowlist of just the screens they need —
 *     e.g. Operations sees only Leaves & SIL and Attendance, never payroll.
 *   · Everyone else: no HRIS access at all (tile hidden, routes blocked).
 *
 * IMPORTANT — this gates the APP UI only. It is not a substitute for
 * SharePoint permissions: an account with read access to the HR-PAYROLL site
 * lists can still reach that data via the SharePoint API or the legacy page.
 * To fully prevent leakage, also restrict the payroll lists/site in SharePoint.
 *
 * WHY IT IS ITS OWN FILE. The hook next door needs MSAL, and MSAL constructs
 * at import — which is enough to make an access-control test die on a missing
 * tenant id before its first assertion. Access control is exactly the thing
 * worth testing, so the rules are pure here and take the role grant as an
 * argument; `hrisAccess.ts` supplies it and re-exports all of this.
 *
 * Both lists are overridable via env (comma-separated) without a code change:
 *   VITE_HRIS_FULL_ACCESS_USERS
 *   VITE_HRIS_LIMITED_ACCESS  ("email=leaves|attendance,email2=leaves")
 */
import { HRIS_SCREENS, type HrisScreenKey } from "./hrisStyleVars";
import { type IResolvedGrant } from "./hrisRoleModel";

const lc = (s?: string) => (s ?? "").trim().toLowerCase();
const ALL_KEYS: HrisScreenKey[] = HRIS_SCREENS.map((s) => s.key);
const VALID = new Set<string>(ALL_KEYS);

/** Accounts with FULL access to every HRIS screen (owner + HR/Finance). */
export const HRIS_FULL_ACCESS_USERS: string[] = (() => {
  const env = import.meta.env?.VITE_HRIS_FULL_ACCESS_USERS as string | undefined;
  if (env) return env.split(",").map(lc).filter(Boolean);
  return [
    "mikko@intalgarmentsopc.onmicrosoft.com",
    "intalmiks11@gmail.com",
    "finance1@intalgarmentsopc.onmicrosoft.com", // Lailani Zubiaga
    "jpgalvez@intalgarmentsopc.onmicrosoft.com", // Jae Galvez
    "managingowner@intalgarmentsopc.onmicrosoft.com", // the owner, Marvin Intal
  ];
})();

/** Per-user LIMITED access: email → the only screens they may open. */
export const HRIS_LIMITED_ACCESS: Record<string, HrisScreenKey[]> = (() => {
  const env = import.meta.env?.VITE_HRIS_LIMITED_ACCESS as string | undefined;
  if (env) {
    const out: Record<string, HrisScreenKey[]> = {};
    for (const entry of env.split(",")) {
      const [email, screens] = entry.split("=");
      const e = lc(email);
      if (!e || !screens) continue;
      out[e] = screens.split("|").map(lc).filter((k) => VALID.has(k)) as HrisScreenKey[];
    }
    return out;
  }
  return {
    "operations@intalgarmentsopc.onmicrosoft.com": ["leaves", "attendance"],
  };
})();

/**
 * Accounts allowed to open the KPI Scoreboard.
 *
 * This is a SEPARATE gate from FULL access, deliberately. The Scoreboard shows
 * every employee's self-assessed performance, their self-declared weaknesses
 * and their corrective-action commitments — a narrower audience than payroll,
 * which HR must administer daily. FULL HRIS access does NOT imply Scoreboard
 * access; it has to be granted here explicitly.
 *
 * Gating it this way (rather than enumerating the other screens for HR) means
 * a future HRIS screen can be added without anyone accidentally inheriting
 * the Scoreboard along with it.
 *
 * Override without a code change: VITE_HRIS_SCOREBOARD_USERS (comma-separated).
 */
export const HRIS_SCOREBOARD_USERS: string[] = (() => {
  const env = import.meta.env?.VITE_HRIS_SCOREBOARD_USERS as string | undefined;
  if (env) return env.split(",").map(lc).filter(Boolean);
  return [
    "mikko@intalgarmentsopc.onmicrosoft.com",
    "intalmiks11@gmail.com",
  ];
})();

/**
 * Accounts allowed to APPROVE or REJECT a leave request.
 *
 * A THIRD separate gate, for the same reason the Scoreboard is a second one:
 * deciding somebody's leave and reading their net pay are different
 * privileges. Approval used to be `access.full`, which meant the only way to
 * let Operations action a request was to hand them the whole payroll — and
 * which left the built-in Supervisor role advertising "Approves leave" while
 * being unable to approve anything (Mikko, 2026-09-19).
 *
 * This list is the fallback for accounts with no Roles & Users assignment.
 * A role that carries `mayDecideLeave` grants it too, so the day-to-day
 * answer lives in the app rather than in a deploy.
 *
 * Seeing the Leaves screen is still required — `hrisAccessFor` ANDs the two,
 * so an email here that cannot open Leaves gains nothing.
 *
 * Override without a code change: VITE_HRIS_LEAVE_APPROVERS (comma-separated).
 */
export const HRIS_LEAVE_APPROVERS: string[] = (() => {
  const env = import.meta.env?.VITE_HRIS_LEAVE_APPROVERS as string | undefined;
  if (env) return env.split(",").map(lc).filter(Boolean);
  return [
    "mikko@intalgarmentsopc.onmicrosoft.com",
    "intalmiks11@gmail.com",
    "finance1@intalgarmentsopc.onmicrosoft.com",
    "operations@intalgarmentsopc.onmicrosoft.com",
  ];
})();

/** Route path for each screen key. */
export const HRIS_ROUTE: Record<HrisScreenKey, string> = {
  dashboard: "/hris",
  computation: "/hris/computation",
  payroll: "/hris/payroll",
  bankfile: "/hris/bankfile",
  benefits: "/hris/benefits",
  thirteenth: "/hris/thirteenth",
  periods: "/hris/periods",
  payslips: "/hris/payslips",
  reports: "/hris/reports",
  employees: "/hris/employees",
  attendance: "/hris/attendance",
  leaves: "/hris/leaves",
  loans: "/hris/loans",
  scoreboard: "/hris/scoreboard",
};

/** Map a /hris route path to its screen key (settings/discover excluded). */
export function screenKeyForPath(pathname: string): HrisScreenKey | null {
  for (const key of ALL_KEYS) {
    const route = HRIS_ROUTE[key];
    if (route === "/hris" ? pathname === "/hris" : pathname.startsWith(route)) return key;
  }
  return null;
}

export interface HrisAccess {
  /** Full access to every screen (incl. Settings & Discovery). */
  full: boolean;
  /** Has access to at least one screen. */
  hasAny: boolean;
  /** The set of screen keys this account may open. */
  screens: Set<HrisScreenKey>;
  /** Allowed screen keys in nav order (for redirect / landing). */
  allowedOrder: HrisScreenKey[];
  /**
   * May APPROVE or REJECT a leave request — not merely read the list.
   *
   * Always implies `screens.has("leaves")`: a decision on a request you cannot
   * see is not a permission anybody meant to grant.
   */
  mayDecideLeave: boolean;
}

/**
 * The payroll money screens — Payroll Computation and Bank File — are for these accounts ONLY,
 * whatever a role or the full-access list says. Salaries, bank accounts and net pay sit on them.
 * Override without a code change: VITE_HRIS_PAYROLL_USERS (comma-separated).
 */
export const HRIS_PAYROLL_USERS: string[] = (() => {
  const env = import.meta.env?.VITE_HRIS_PAYROLL_USERS as string | undefined;
  if (env) return env.split(",").map(lc).filter(Boolean);
  return [
    "mikko@intalgarmentsopc.onmicrosoft.com",
    "intalmiks11@gmail.com",
    "finance1@intalgarmentsopc.onmicrosoft.com", // Lailani Zubiaga
    "managingowner@intalgarmentsopc.onmicrosoft.com", // the owner, Marvin Intal
    "jpgalvez@intalgarmentsopc.onmicrosoft.com", // Jae Galvez
  ];
})();
export const PAYROLL_ONLY_SCREENS: HrisScreenKey[] = ["computation", "bankfile"];

export function resolveHrisAccess(email: string | undefined, granted: IResolvedGrant | null): HrisAccess {
  const a = resolveBase(email, granted);
  if (email && HRIS_PAYROLL_USERS.includes(lc(email))) return a;
  const screens = new Set([...a.screens].filter((k) => !PAYROLL_ONLY_SCREENS.includes(k)));
  return { ...a, screens, allowedOrder: a.allowedOrder.filter((k) => screens.has(k)) };
}

function resolveBase(email: string | undefined, granted: IResolvedGrant | null): HrisAccess {
  const e = lc(email);
  const mayScore = !!e && HRIS_SCOREBOARD_USERS.includes(e);

  /**
   * A blanket "full access" never carries the Scoreboard. HR administers
   * payroll daily and needs every other screen; performance self-assessments
   * are a narrower audience. The screen is granted explicitly — either by the
   * allowlist above, or by a Roles & Users role that names it individually.
   */
  const gateFull = (keys: HrisScreenKey[]) =>
    mayScore ? keys : keys.filter((k) => k !== "scoreboard");

  // The built-in FULL list always wins, so a Roles & Users edit can never lock
  // the owner or Finance out of the module that maintains it.
  if (e && HRIS_FULL_ACCESS_USERS.includes(e)) {
    const keys = gateFull([...ALL_KEYS]);
    // `full` stays true so Settings and Discovery remain reachable — it means
    // "administers HRIS", not "sees every screen".
    return { full: true, hasAny: true, screens: new Set(keys), allowedOrder: keys, mayDecideLeave: true };
  }

  // Then the Roles & Users assignment, when one exists for this account.
  if (granted) {
    // A `full` role is treated like the built-in list; a role that picks
    // screens one by one is taken at its word, Scoreboard included.
    const keys = granted.full
      ? gateFull([...ALL_KEYS])
      : ALL_KEYS.filter((k) => granted.screens.has(k) || (k === "scoreboard" && mayScore));
    const screens = new Set(keys);
    return {
      full: granted.full,
      hasAny: granted.full || keys.length > 0,
      screens,
      allowedOrder: keys,
      // The role's own capability, OR the standing allowlist — an account
      // named there keeps its approval when somebody assigns it a role.
      mayDecideLeave:
        (granted.mayDecideLeave || (!!e && HRIS_LEAVE_APPROVERS.includes(e))) && screens.has("leaves"),
    };
  }

  // No assignment → the previous hard-coded behaviour, plus the Scoreboard for
  // an account on its allowlist. That combination is the intended shape for an
  // executive who should see performance and no other HRIS screen.
  const limited = (e && HRIS_LIMITED_ACCESS[e]) || [];
  const set = new Set<HrisScreenKey>(limited.filter((k) => k !== "scoreboard"));
  if (mayScore) set.add("scoreboard");
  const allowedOrder = ALL_KEYS.filter((k) => set.has(k));
  return {
    full: false,
    hasAny: allowedOrder.length > 0,
    screens: set,
    allowedOrder,
    mayDecideLeave: !!e && HRIS_LEAVE_APPROVERS.includes(e) && set.has("leaves"),
  };
}

/** First screen route this account may land on, or null if none. */
export function firstAllowedRoute(access: HrisAccess): string | null {
  const first = access.allowedOrder[0];
  return first ? HRIS_ROUTE[first] : null;
}
